import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import rehypeDocument from 'rehype-document';
import rehypeKatex from 'rehype-katex';
import rehypePrismPlus from 'rehype-prism-plus';
import rehypeSlug from 'rehype-slug';
import rehypeStringify from 'rehype-stringify';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import type { Configuration, Node } from '../types';
import { RenderError } from '../types';
import { Logger } from '../utils/Logger';
import type { TOCGenerator } from '../utils/TOCGenerator.js';
import { getMermaidCSS, getMermaidScript, rehypeMermaid } from './rehype-mermaid.js';

/**
 * HTMLRenderer converts Markdown AST to HTML using unified/rehype ecosystem
 *
 * Features:
 * - AST to HTML conversion via rehype
 * - Mathematical expression rendering (KaTeX)
 * - Code syntax highlighting (Prism)
 * - Automatic heading IDs and links
 * - Full HTML document generation
 * - Custom stylesheet injection
 *
 * @example
 * ```typescript
 * const renderer = new HTMLRenderer(config);
 * const html = await renderer.render(ast);
 * ```
 */
export class HTMLRenderer {
  private config: Configuration;
  private processor: any; // Using any for unified processor flexibility
  private logger: Logger;
  private tocGenerator?: TOCGenerator;

  constructor(config: Configuration) {
    this.config = config;
    this.logger = new Logger(config.logging);
    this.processor = this.buildProcessor();

    // TOC generator will be initialized lazily when needed
    this.logger.debug('HTMLRenderer initialized', {
      theme: config.theme,
      mathEnabled: config.features.math?.enabled,
      codeHighlightEnabled: config.features.codeHighlight?.enabled,
      tocEnabled: config.features.toc?.enabled,
    });
  }

  /**
   * Initialize TOC generator lazily using dynamic imports
   * @private
   */
  private async initializeTOCGenerator(): Promise<void> {
    if (this.tocGenerator || !this.config.features.toc?.enabled) {
      return;
    }

    try {
      const { TOCGenerator } = await import('../utils/TOCGenerator.js');
      this.tocGenerator = new TOCGenerator(this.config);
      this.logger.debug('TOC generator initialized via dynamic import');
    } catch (error) {
      this.logger.error('Failed to initialize TOC generator', error as Error);
    }
  }

  /**
   * Render Markdown AST to HTML
   * @param ast - Parsed markdown AST from MarkdownParser
   * @returns Promise<string> - Complete HTML document
   * @throws RenderError - When rendering fails
   */
  async render(ast: Node): Promise<string> {
    const startTime = Date.now();
    this.logger.trace('Starting HTML rendering', { astType: ast.type });

    try {
      // Initialize TOC generator if needed
      await this.initializeTOCGenerator();

      // Convert AST to HTML using rehype
      const htmlAst = await this.processor.run(ast);
      let html = (await this.processor.stringify(htmlAst)) as string;

      // Insert TOC if enabled and generator is available
      if (this.tocGenerator && this.config.features.toc?.enabled) {
        const tocHtml = this.tocGenerator.generate(ast);
        if (tocHtml) {
          // Insert TOC after body tag or at configured position
          html = this.insertTOC(html, tocHtml);
          this.logger.debug('TOC inserted into HTML document');
        }
      }

      const renderTime = Date.now() - startTime;
      this.logger.info('HTML rendering completed successfully', {
        htmlLength: html.length,
        renderTimeMs: renderTime,
        theme: this.config.theme,
        tocGenerated: !!this.tocGenerator,
      });

      return html;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown rendering error';
      this.logger.error('HTML rendering failed', error as Error);
      throw new RenderError(`Failed to render HTML: ${errorMessage}`, {
        cause: error as Error,
      });
    }
  }

  /**
   * Insert TOC into HTML document at configured position
   * @param html - HTML document string
   * @param tocHtml - TOC HTML to insert
   * @returns HTML with TOC inserted
   */
  private insertTOC(html: string, tocHtml: string): string {
    const position = this.config.features.toc.position;

    switch (position) {
      case 'after-cover':
      case 'top':
        // Insert after opening body tag
        return html.replace(/<body[^>]*>/, `$&\n${tocHtml}\n`);

      case 'before-content':
        // Insert before first content heading
        return html.replace(/(<h[1-6][^>]*>)/, `${tocHtml}\n\n$1`);

      default:
        // Default to after body tag
        return html.replace(/<body[^>]*>/, `$&\n${tocHtml}\n`);
    }
  }

  /**
   * Build the unified processor with rehype plugins
   * @returns Configured processor instance
   */
  private buildProcessor(): any {
    let processor: any = unified()
      // Convert markdown AST to HTML AST
      .use(remarkRehype, { allowDangerousHtml: true });

    // Add heading IDs for anchor links
    processor = processor.use(rehypeSlug);

    // Add automatic heading links
    processor = processor.use(rehypeAutolinkHeadings, {
      behavior: 'wrap',
      properties: { className: ['heading-anchor'] },
    });

    // Add math rendering if enabled
    if (this.config.features.math?.enabled) {
      processor = processor.use(rehypeKatex, {
        fleqn: false,
        throwOnError: false,
        strict: false,
      });
    }

    // Add Mermaid diagram support if enabled (before code highlighting)
    if (this.config.features.diagrams?.mermaid) {
      console.log('[HTMLRenderer] Mermaid diagrams enabled, adding rehypeMermaid plugin');
      processor = processor.use(rehypeMermaid);
      console.log('[HTMLRenderer] rehypeMermaid plugin added to processor');
    } else {
      console.log('[HTMLRenderer] Mermaid diagrams NOT enabled in config');
    }

    // Add code highlighting if enabled
    if (this.config.features.codeHighlight?.enabled) {
      processor = processor.use(rehypePrismPlus, {
        showLineNumbers: this.config.features.codeHighlight.showLineNumbers || false,
        ignoreMissing: true,
      });
    }

    // Wrap in complete HTML document
    processor = processor.use(rehypeDocument, {
      title: this.config.document.title || 'Document',
      language: this.config.document.language || 'en',
      css: this.getStylesheets(),
      meta: this.getMetaTags(),
    });

    // Convert to final HTML string
    processor = processor.use(rehypeStringify, {
      allowDangerousHtml: true,
    });

    this.logConfiguration();
    return processor;
  }

  /**
   * Get stylesheets for HTML document
   * @returns Array of stylesheet URLs/paths or CSS content
   */
  private getStylesheets(): string[] {
    const stylesheets: string[] = [];

    // Add feature-specific CSS content when features are enabled
    if (this.config.features.math?.enabled) {
      stylesheets.push('/* KaTeX CSS for math rendering */');
    }

    if (this.config.features.codeHighlight?.enabled) {
      stylesheets.push('/* Prism.js syntax highlighting - GitHub theme */');
    }

    // Custom CSS files
    if (this.config.customCSS && this.config.customCSS.length > 0) {
      this.logger.debug('Adding custom CSS files to stylesheets', {
        files: this.config.customCSS,
        count: this.config.customCSS.length,
      });
      stylesheets.push(...this.config.customCSS);
    }

    this.logger.trace('Stylesheets prepared for rehype-document', {
      count: stylesheets.length,
      includesFeatureCSS:
        this.config.features.math?.enabled || this.config.features.codeHighlight?.enabled,
    });

    return stylesheets;
  }

  /**
   * Get meta tags for HTML document
   * @returns Object with meta tag properties
   */
  private getMetaTags(): Record<string, string> {
    const meta: Record<string, string> = {
      viewport: 'width=device-width, initial-scale=1',
      charset: 'utf-8',
    };

    // Document metadata
    if (this.config.document.author) {
      meta.author = this.config.document.author;
    }

    if (this.config.document.subject) {
      meta.description = this.config.document.subject;
    }

    if (this.config.document.keywords?.length) {
      meta.keywords = this.config.document.keywords.join(', ');
    }

    return meta;
  }

  /**
   * Get Mermaid scripts and styles for HTML document
   * @returns HTML string with Mermaid setup
   */
  getMermaidResources(): string {
    if (!this.config.features.diagrams?.mermaid) {
      return '';
    }
    return getMermaidCSS() + getMermaidScript();
  }

  /**
   * Log current configuration for debugging
   */
  private logConfiguration(): void {
    const features = [];
    if (this.config.features.math?.enabled) features.push('Math');
    if (this.config.features.codeHighlight?.enabled) features.push('CodeHighlight');
    if (this.config.features.diagrams?.mermaid) features.push('Mermaid');

    const stylesheetCount = this.getStylesheets().length;

    this.logger.info('HTMLRenderer configuration', {
      theme: this.config.theme,
      stylesheetCount,
      features,
      documentTitle: this.config.document.title,
      language: this.config.document.language,
    });
  }

  /**
   * Get the configured processor for external use
   * @returns The unified processor instance
   */
  getProcessor(): any {
    return this.processor;
  }

  /**
   * Render HTML fragment (without document wrapper)
   * @param ast - Parsed markdown AST
   * @returns Promise<string> - HTML fragment
   */
  async renderFragment(ast: Node): Promise<string> {
    try {
      // Create a processor without document wrapper
      const fragmentProcessor = unified()
        .use(remarkRehype, { allowDangerousHtml: true })
        .use(rehypeSlug);

      // Add math rendering if enabled
      if (this.config.features.math?.enabled) {
        fragmentProcessor.use(rehypeKatex, {
          fleqn: false,
          throwOnError: false,
          strict: false,
        });
      }

      // Add Mermaid diagram support if enabled
      if (this.config.features.diagrams?.mermaid) {
        fragmentProcessor.use(rehypeMermaid);
      }

      // Add code highlighting if enabled
      if (this.config.features.codeHighlight?.enabled) {
        fragmentProcessor.use(rehypePrismPlus, {
          showLineNumbers: this.config.features.codeHighlight.showLineNumbers || false,
          ignoreMissing: true,
        });
      }

      // Convert to HTML string
      fragmentProcessor.use(rehypeStringify, { allowDangerousHtml: true });

      const htmlAst = await fragmentProcessor.run(ast as any);
      const html = await fragmentProcessor.stringify(htmlAst);
      return html as string;
    } catch (error) {
      throw new RenderError('Failed to render HTML fragment', {
        cause: error as Error,
      });
    }
  }

  /**
   * Get document title from AST or config
   * @param ast - Parsed markdown AST
   * @returns Document title
   */
  getDocumentTitle(ast: Node): string {
    // Try to get title from frontmatter first
    if ((ast as any).frontmatter?.title) {
      return (ast as any).frontmatter.title;
    }

    // Try to find first heading in content
    if (ast.children) {
      for (const child of ast.children) {
        if (child.type === 'heading' && child.children) {
          // Extract text content from heading children
          const headingText = this.extractTextFromNode(child);
          if (headingText) return headingText;
        }
      }
    }

    // Fall back to config or default
    return this.config.document.title || 'Document';
  }

  /**
   * Extract text content from AST node
   * @param node - AST node
   * @returns Extracted text content
   */
  private extractTextFromNode(node: Node): string {
    if (node.type === 'text' && node.value) {
      return node.value;
    }

    if (node.children) {
      return node.children.map((child) => this.extractTextFromNode(child)).join('');
    }

    return '';
  }
}
